// components/modals/MeetingsContent.js

import React, { useState, useEffect, useMemo } from 'react';
import { 
    FiPlus, FiEdit, FiTrash2, FiLoader, FiCalendar, FiClock, 
    FiUsers, FiRefreshCw, FiSearch, FiCheckCircle, FiXCircle, 
    FiPlayCircle, FiServer, FiAlertCircle 
} from 'react-icons/fi';
import { API_URL } from '../../services/api';
import { DataState } from '../../context/DataProvider';
import toast from 'react-hot-toast';
import MeetingFormModal from './MeetingFormModal'; 
import * as meetingsApi from '../../services/meetingsApi';

// --- NEW DUMMY API & DATA STRUCTURE ---
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const sampleLLMSummary = {
    summary: "This was a stand-up meeting focused on testing. The main goal is to correctly process transcripts. A key challenge identified is mapping attendees.",
    action_items: [
        "Process recurring event transcripts daily.",
        "Use Teams API to map meeting attendees to their actual team roles."
    ],
    user_updates: [
        { user: "Aditya Sharma", yesterday_done: "Tested basic transcript functionality.", today_plan: "Ensure recurring events are processed.", blockers: "None." },
    ]
};

let dummyData = {
    meetings: [
        {
            id: 'm1',
            title: 'Daily Stand-up',
            description: 'Daily sync for the Phoenix project team.',
            startTime: new Date(new Date().setHours(9, 30, 0, 0)).toISOString(),
            duration: 15,
            participants: [{ id: 'u1', displayName: 'Sarthak Chaudhary' }, { id: 'u2', displayName: 'Jane Doe' }],
            isRecurring: true,
            meetingEvents: [
                {
                    id: 'e1',
                    date: '2025-10-28',
                    status: 'processed', 
                    llm_summary: sampleLLMSummary, 
                    transcripts: [
                        { id: 't1', filename: 'standup_28_10_part1.vtt' },
                        { id: 't2', filename: 'standup_28_10_part2.vtt' }
                    ]
                },
                { id: 'e2', date: '2025-10-27', status: 'processing', llm_summary: null, transcripts: [{ id: 't3', filename: 'standup_27_10.vtt' }] },
                { id: 'e3', date: '2025-10-26', status: 'failed', llm_summary: null, transcripts: [] },
            ]
        },
        {
            id: 'm2',
            title: 'Sprint Planning - "Phoenix"',
            description: 'Plan tasks for the next 2-week sprint.',
            startTime: new Date(Date.now() + 86400000 * 3).toISOString(),
            duration: 60,
            participants: [{ id: 'u1', displayName: 'Sarthak Chaudhary' }, { id: 'u3', displayName: 'Bob Smith' }],
            isRecurring: false,
            meetingEvents: [
                { id: 'e4', date: '2025-11-01', status: 'pending', llm_summary: null, transcripts: [] }
            ]
        }
    ],
    users: [
        { id: 'u1', displayName: 'Sarthak Chaudhary', email: 'sarthak@example.com' },
        { id: 'u2', displayName: 'Jane Doe', email: 'jane@example.com' },
        { id: 'u3', displayName: 'Bob Smith', email: 'bob@example.com' },
        { id: 'u4', displayName: 'Alice Johnson', email: 'alice@example.com' },
    ]
};
let nextIds = { meeting: 3, event: 5, transcript: 4 };

const MeetingsContent = () => {
    const { userGoogleId: accessToken } = DataState();
    const [meetings, setMeetings] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingMeeting, setEditingMeeting] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    
    // --- UI State for Search ---
    const [searchQuery, setSearchQuery] = useState("");

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [meetingsData, usersData] = await Promise.all([
                meetingsApi.listMeetings(accessToken),
                meetingsApi.listUsers(accessToken)
            ]);
            setMeetings(meetingsData);
            setAllUsers(usersData);
        } catch (err) {
            toast.error("Failed to load meetings or users.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [accessToken]);

    const handleAddMeeting = () => {
        setEditingMeeting({
            title: '',
            description: '',
            startTime: new Date().toISOString().slice(0, 16),
            duration: 60,
            attendees: [],  // RENAMED
            organizer: null, // +++ ADDED
            isRecurring: false,
            meetingEvents: []
        });
        setIsFormOpen(true);
    };

    const handleEditMeeting = (meeting) => {
        // setEditingMeeting(meeting);
        const formattedMeeting = {
            ...meeting,
            // Format attendees list for Multi-Select
            attendees: meeting.attendees.map(p => ({ ...p, value: p.id, label: p.displayName })),
            // Format organizer for Single-Select
            organizer: meeting.organizer ? { ...meeting.organizer, value: meeting.organizer.id, label: meeting.organizer.displayName } : null
        };
        setEditingMeeting(formattedMeeting);
        setIsFormOpen(true);
    };

    const handleDeleteMeeting = async (meetingId) => {
        if (!window.confirm("Are you sure you want to delete this meeting series?")) return;
        setProcessingId(meetingId);
        try {
            await meetingsApi.deleteMeeting(accessToken, meetingId);
            setMeetings(prev => prev.filter(m => m.id !== meetingId));
            toast.success("Meeting deleted.");
        } catch (err) {
            toast.error("Failed to delete meeting.");
        } finally {
            setProcessingId(null);
        }
    };

    const handleSaveMeeting = (updatedMeeting) => {
        // This function is now just for updating the list,
        // the API call will happen inside MeetingFormModal
        setMeetings(prev => prev.map(m => m.id === updatedMeeting.id ? updatedMeeting : m));

        // If it's a new meeting, add it
        if (!meetings.find(m => m.id === updatedMeeting.id)) {
            setMeetings(prev => [updatedMeeting, ...prev]);
        }

        setIsFormOpen(false);
        setEditingMeeting(null);
    };

    const formatDateTime = (isoString) => {
        return new Date(isoString).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short'
        });
    };

    // --- Filter Logic ---
    const filteredMeetings = useMemo(() => {
        if (!searchQuery) return meetings;
        const lowerQuery = searchQuery.toLowerCase();
        return meetings.filter(m => 
            m.title.toLowerCase().includes(lowerQuery) || 
            (m.description && m.description.toLowerCase().includes(lowerQuery))
        );
    }, [meetings, searchQuery]);

    // --- HELPER COMPONENT: Status Badge ---
    const StatusBadge = ({ count, type, label, icon: Icon }) => {
        if (!count) return null;
        
        const styles = {
            processed: "text-emerald-400 bg-emerald-950/30 border-emerald-900/50",
            queued: "text-cyan-400 bg-cyan-950/30 border-cyan-900/50",
            processing: "text-amber-400 bg-amber-950/30 border-amber-900/50",
            reprocessing: "text-orange-400 bg-orange-950/30 border-orange-900/50",
            pending: "text-blue-400 bg-blue-950/30 border-blue-900/50",
            failed: "text-red-400 bg-red-950/30 border-red-900/50"
        };

        return (
            <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-medium ${styles[type] || styles.pending}`}>
                <Icon className="w-2.5 h-2.5" />
                <span>{count} {label}</span>
            </div>
        );
    };

    // --- UPDATED SUMMARY: Counts statuses of Meeting Events ---
    const getEventSummary = (events) => {
        if (!events || events.length === 0) {
            return <span className="text-[10px] text-zinc-600 italic">No events recorded</span>;
        }
        const counts = events.reduce((acc, e) => {
            acc[e.status] = (acc[e.status] || 0) + 1;
            return acc;
        }, {});

        return (
            <div className="flex flex-wrap gap-1.5 mt-1">
                <StatusBadge count={counts.processed} type="processed" label="Done" icon={FiCheckCircle} />
                <StatusBadge count={counts.queued} type="queued" label="Queued" icon={FiServer} />
                <StatusBadge count={counts.processing} type="processing" label="Running" icon={FiLoader} />
                <StatusBadge count={counts.reprocessing} type="reprocessing" label="Retrying" icon={FiRefreshCw} />
                <StatusBadge count={counts.pending} type="pending" label="Pending" icon={FiPlayCircle} />
                <StatusBadge count={counts.failed} type="failed" label="Failed" icon={FiXCircle} />
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full max-h-screen p-1">
            {/* --- Modals --- */}
            {isFormOpen && (
                <MeetingFormModal
                    isOpen={isFormOpen}
                    onClose={() => setIsFormOpen(false)}
                    onSave={handleSaveMeeting} // This callback updates the list
                    meeting={editingMeeting}
                    allUsers={allUsers}
                    // dummyApi={dummyApi} // Pass the api object
                    accessToken={accessToken}
                />
            )}

            {/* --- Compact Header --- */}
            <div className="flex flex-col items-center justify-between gap-3 px-1 mb-3 md:flex-row">
                <div>
                    <h3 className="flex items-center gap-2 text-base font-bold text-zinc-100">
                        <FiCalendar className="w-4 h-4 text-purple-500" />
                        Meetings
                    </h3>
                    <p className="text-[10px] text-zinc-500">Manage recurring series and transcripts.</p>
                </div>
                
                <div className="flex items-center w-full gap-2 md:w-auto">
                    <button 
                        onClick={() => fetchAllData()} 
                        disabled={loading}
                        className="p-1.5 transition-colors border rounded text-zinc-400 hover:text-zinc-100 bg-zinc-800/50 border-zinc-700 hover:bg-zinc-700 disabled:opacity-50"
                        title="Refresh"
                    >
                        <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>

                    <button 
                        onClick={handleAddMeeting} 
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 hover:bg-[#5652CF] text-white rounded text-[11px] font-medium shadow-sm transition-all hover:-translate-y-0.5"
                    >
                        <FiPlus className="w-3 h-3" /> Add Meeting
                    </button>
                </div>
            </div>

            {/* --- Content Card --- */}
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden border shadow-sm bg-[#121214] border-zinc-800 rounded-lg">
                
                {/* Slim Toolbar */}
                <div className="flex items-center gap-3 px-3 py-2 border-b border-zinc-800 bg-zinc-900/30">
                    <div className="relative flex-1 max-w-xs">
                        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
                        <input 
                            type="text" 
                            placeholder="Search meetings..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full py-1.5 pr-2 text-[11px] transition-all border rounded bg-zinc-900 border-zinc-700 text-zinc-200 pl-8 focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50 focus:border-[#5652CF] placeholder:text-zinc-600"
                        />
                    </div>
                    <div className="hidden ml-auto text-[10px] text-zinc-500 md:block">
                        <span className="font-semibold text-zinc-300">{meetings.length}</span> Series
                    </div>
                </div>

                {/* Meetings List Container */}
                <div className="flex-1 p-2 space-y-2 overflow-auto custom-scrollbar bg-zinc-900/10">
                    {loading ? (
                        <div className="flex items-center justify-center h-32"><FiLoader className="text-xl text-[#5652CF] animate-spin" /></div>
                    ) : filteredMeetings.length === 0 ? (
                        <div className="py-12 text-center">
                            <div className="inline-block p-2 mb-2 rounded-full bg-zinc-800/50">
                                <FiCalendar className="w-5 h-5 text-zinc-600" />
                            </div>
                            <p className="text-xs font-medium text-zinc-500">No meetings found.</p>
                            <p className="text-[10px] text-zinc-600 mt-0.5">Click "Add Meeting" to get started.</p>
                        </div>
                    ) : (
                        filteredMeetings.sort((a, b) => new Date(b.startTime) - new Date(a.startTime)).map(meeting => (
                            <div key={meeting.id} className="p-3 transition-all duration-200 border rounded-md group bg-zinc-800/40 hover:bg-zinc-800/60 border-zinc-800 hover:border-zinc-700">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1 min-w-0 pr-3">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <h3 className="text-xs font-bold truncate text-zinc-200">{meeting.title}</h3>
                                            {meeting.isRecurring && (
                                                <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-purple-900/30 text-purple-400 border border-purple-900/50 flex items-center gap-0.5">
                                                    <FiRefreshCw className="w-2 h-2" /> Recurring
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-zinc-500 line-clamp-1">{meeting.description || "No description provided."}</p>
                                    </div>
                                    <div className="flex items-center flex-shrink-0 gap-1 transition-opacity opacity-0 group-hover:opacity-100">
                                        <button onClick={() => handleEditMeeting(meeting)} title="Edit" className="p-1 transition-colors rounded text-zinc-400 hover:text-blue-400 hover:bg-[#5652CF]/10">
                                            <FiEdit className="w-3 h-3" />
                                        </button>
                                        <button onClick={() => handleDeleteMeeting(meeting.id)} disabled={processingId === meeting.id} title="Delete" className="p-1 transition-colors rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10 disabled:cursor-wait">
                                            {processingId === meeting.id ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiTrash2 className="w-3 h-3" />}
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 mb-3 md:grid-cols-4">
                                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 bg-zinc-900/50 px-2 py-1 rounded border border-zinc-800/50" title="Start Time">
                                        <FiCalendar className="w-3 h-3 text-zinc-500" />
                                        <span className="truncate">{formatDateTime(meeting.startTime)}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 bg-zinc-900/50 px-2 py-1 rounded border border-zinc-800/50" title="Duration">
                                        <FiClock className="w-3 h-3 text-zinc-500" />
                                        <span>{meeting.duration} min</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 bg-zinc-900/50 px-2 py-1 rounded border border-zinc-800/50 md:col-span-2" title="Attendees"> 
                                        <FiUsers className="w-3 h-3 text-zinc-500" />
                                        <span className="truncate">{meeting.attendees?.length || 0} attendees</span> 
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-zinc-800/50">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider">Processing Status</h4>
                                    </div>
                                    {getEventSummary(meeting.meetingEvents)}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default MeetingsContent;